import {useMemo} from 'react';
import {UrlLink} from '../../../../applink/UrlLink';
import {PromotionBannerContainer} from '../../../../common/containers/PromotionBannerContainer';
import {t} from '../../../../common/getTranslations';
import {TabPositionSticky} from '../../../../common/presentational/TabPositionSticky';
import {formatWithCommas} from '../../../../common/shared/utils/formatWithCommas';
import {twMerge} from '../../../../common/twMerge';
import {DayOfWeekBmType} from '../../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {PageViewCustomPropsDataKey} from '../../../../logging/PageViewCustomPropsDataKey.enum';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {IconType} from '../../../atoms/Icon/IconType.enum';
import {BelowDefaultTab} from '../../../organisms/BelowDefaultTab/BelowDefaultTab';
import {DayOfWeekTab} from '../../../organisms/DayOfWeekTab/DayOfWeekTab';
import {Dialog} from '../../../organisms/Dialog';
import {HeaderTitle} from '../../../organisms/HeaderTitle/HeaderTitle';
import {StaticLandingDayOfWeekProps} from './StaticLandingDayOfWeekProps.interface';

export function StaticLandingDayOfWeek({
  isMobile,
  isWebView,
  isSubtab,
  totalCount,
  dayTabList,
  currentDayTabUid,
  bmList,
  currentBmType,
  currentSubcategoryUid,
  subcategoryList,
  isShowSubcategoryDialog,
  promotionBanner,
  className,
  showSubcategoryDialog,
  hideSubcategoryDialog,
  onClickSubcategoryItem,
  onClickBmModelItem,
  onClickDayOfWeekTabItem,
  children,
  ...props
}: StaticLandingDayOfWeekProps) {
  const currentSubcategoryName = useMemo(
    () =>
      subcategoryList?.find(item => item.param === currentSubcategoryUid)?.name,
    [currentSubcategoryUid, subcategoryList]
  );
  const currentBmTypeName = useMemo(
    () => bmList?.find(item => item.param === currentBmType)?.name,
    [bmList, currentBmType]
  );
  const currentDayName = useMemo(
    () => dayTabList?.find(item => item.param === currentDayTabUid)?.name,
    [currentDayTabUid, dayTabList]
  );
  const hasSubcategory = subcategoryList && subcategoryList.length > 0;

  return (
    <div
      className={twMerge('flex flex-col w-full grow', className)}
      {...putTiaraObjectProps({
        customProps: {
          page_genre_filter: currentSubcategoryName,
          page_bm_filter: currentBmTypeName,
          page_day: currentDayName,
        },
        click: {
          layer1: '섹션',
        },
      })}
      {...(isSubtab && {
        [PageViewCustomPropsDataKey.TabType]: 'tab_type=landing_single',
      })}
      {...props}
    >
      {!isMobile && !isSubtab && (
        <HeaderTitle className="mt-20pxr mb-8pxr px-15pxr">
          {t('landing_ongoing')}
        </HeaderTitle>
      )}
      {dayTabList && currentDayTabUid && (
        <TabPositionSticky
          depth={isSubtab ? 3 : 1}
          isMobile={isMobile}
          isWebView={isWebView}
          disabled={!isMobile}
        >
          <DayOfWeekTab
            tabItems={dayTabList}
            currentTabItemParam={currentDayTabUid}
            onClickTabItem={(tabUid, e) => {
              aggregateAndSendLog({
                targetElem: e?.target as HTMLElement,
                actionName: '요일_클릭',
                actionType: 'Event',
                click: {
                  copy: dayTabList.find(dayTab => dayTab.param === tabUid)
                    ?.name,
                },
                customProps: {
                  user_action: 'click',
                },
              });
              onClickDayOfWeekTabItem && onClickDayOfWeekTabItem(tabUid);
            }}
            className="px-10pxr"
            {...putTiaraObjectProps({
              click: {
                layer1: isSubtab ? '서브탭2' : '서브탭',
              },
            })}
          />
        </TabPositionSticky>
      )}
      {(bmList || currentSubcategoryName) && (
        <TabPositionSticky
          depth={isSubtab ? 4 : 2}
          isMobile={isMobile}
          isWebView={isWebView}
          disabled={!isMobile}
        >
          <BelowDefaultTab
            initialSelectLabelIndex={
              bmList
                ? bmList.findIndex(item => item.param === currentBmType)
                : undefined
            }
            className="pr-15pxr"
          >
            {bmList &&
              bmList.map(({name, param, href}) => (
                <BelowDefaultTab.SelectLabel
                  key={param}
                  isActive={currentBmType === param}
                  onClick={e => {
                    LOG_CLICK_EVENT(
                      'BM_클릭',
                      e,
                      () =>
                        onClickBmModelItem &&
                        onClickBmModelItem(param as DayOfWeekBmType)
                    );
                  }}
                  {...{
                    [PageViewCustomPropsDataKey.PageBmFilter]:
                      currentBmType === param
                        ? `page_bm_filter=${name}`
                        : undefined,
                  }}
                  {...putTiaraObjectProps({
                    click: {
                      layer1: '필터',
                      copy: name,
                    },
                  })}
                >
                  <UrlLink url={href}>{name}</UrlLink>
                </BelowDefaultTab.SelectLabel>
              ))}
            {hasSubcategory && currentSubcategoryName && (
              <BelowDefaultTab.SelectBox
                onClick={showSubcategoryDialog}
                {...{
                  [PageViewCustomPropsDataKey.PageGenreFilter]: `page_genre_filter=${currentSubcategoryName}`,
                }}
                iconType={IconType.TabFilter}
              >
                {currentSubcategoryName} ({formatWithCommas(totalCount)})
              </BelowDefaultTab.SelectBox>
            )}
          </BelowDefaultTab>
        </TabPositionSticky>
      )}
      {isSubtab && promotionBanner && (
        <PromotionBannerContainer promotionBanner={promotionBanner} />
      )}
      {hasSubcategory && (
        <Dialog
          isOpen={!!isShowSubcategoryDialog}
          onClose={hideSubcategoryDialog}
          isMobile={isMobile}
          {...putTiaraObjectProps({
            customProps: {
              page_genre_filter: currentSubcategoryName,
              page_bm_filter: currentBmTypeName,
              page_day: currentDayName,
            },
          })}
        >
          <Dialog.Dimmed />
          <Dialog.List className="space-y-0">
            {subcategoryList.map(subcategory => (
              <Dialog.Option
                key={subcategory.id}
                onClick={e =>
                  LOG_CLICK_EVENT('장르필터_클릭', e, () =>
                    onClickSubcategoryItem(subcategory.param)
                  )
                }
                isChecked={subcategory.param === currentSubcategoryUid}
                {...putTiaraObjectProps({
                  click: {
                    layer1: '리스팅옵션',
                    copy: subcategory.name,
                  },
                })}
              >
                {subcategory.name}
              </Dialog.Option>
            ))}
          </Dialog.List>
          <Dialog.BottomCombination>
            <Dialog.Button onClick={hideSubcategoryDialog}>
              {t('common_cancel_button')}
            </Dialog.Button>
          </Dialog.BottomCombination>
        </Dialog>
      )}
      {children}
    </div>
  );
}
